import { useSyncExternalStore } from 'react';
import { getFeatureSession, onSessionChange } from './featureSession';
import { CONSENT_SCOPES, RETIRED_PATH, PRIVACY_NOTICE_VERSION, LEGACY_PRIVACY_NOTICE_VERSION, consentPurposeForRequest } from './requestPolicy';
import { rememberWithdrawal, forgetWithdrawal, retryWithdrawal } from './withdrawalRetry';
import { validateConsentState } from './privateTransport';

export const PRIVACY_STORAGE_KEY = 'meetjoz-privacy-preferences-v1'; // Legacy; never read as permission.
export { PRIVACY_NOTICE_VERSION };
const emptyScopes = () => Object.fromEntries(CONSENT_SCOPES.map(scope => [scope.key, false]));
const EMPTY = Object.freeze({ analytics:false, scopes:Object.freeze(emptyScopes()), pendingScopes:Object.freeze([]), targetScopes:null, decided:true, revision:0, receipts:[] });
let state=EMPTY;
const listeners=new Set();
const emit=()=>listeners.forEach(fn=>fn());
const scopeForPurpose=purpose=>CONSENT_SCOPES.find(scope=>scope.id===purpose)?.key;
const normalizeScopes=value=>Object.fromEntries(CONSENT_SCOPES.map(scope=>[scope.key,value?.[scope.key]===true]));
const applyResult=result=>({
  ...result,
  analytics:false,
  scopes:normalizeScopes(result?.scopes),
  pendingScopes:[],
  targetScopes:null,
  decided:true,
  pendingWithdrawal:false,
});
export const getPrivacyPreferences=()=>state;
export const subscribePrivacyPreferences=fn=>{listeners.add(fn);return()=>listeners.delete(fn);};
export const usePrivacyPreferences=()=>useSyncExternalStore(subscribePrivacyPreferences,getPrivacyPreferences,()=>EMPTY);
let expiryTimer;
let generation=0;
let saveQueue=Promise.resolve();
onSessionChange(()=>{if(!getFeatureSession()){generation++;saveQueue=Promise.resolve();clearTimeout(expiryTimer);state=EMPTY;emit();}});
const scheduleExpiry=expiresAt=>{
  clearTimeout(expiryTimer);
  if(!expiresAt||!Number.isFinite(Date.parse(expiresAt))) return;
  expiryTimer=setTimeout(()=>{generation++;state={...state,scopes:emptyScopes(),targetScopes:null,pendingScopes:[]};emit();},Math.max(0,Date.parse(expiresAt)-Date.now()));
};

// A single service changes only its own target, even while other saves are in
// flight. Full snapshots are reserved for the master switch.
export async function savePrivacyPreferences({scopes:requestedScopes,scopeChanges}={}) {
  const previousTarget=normalizeScopes(state.targetScopes||state.scopes);
  const desired=normalizeScopes(scopeChanges?{...previousTarget,...scopeChanges}:requestedScopes);
  const before=normalizeScopes(state.scopes);
  const wasPending=Boolean(state.targetScopes);
  const changed=CONSENT_SCOPES.filter(scope=>desired[scope.key]!==previousTarget[scope.key]||desired[scope.key]!==before[scope.key]);
  if(!changed.length&&!wasPending) return state;
  const requestGeneration=++generation;
  const withdrawn=CONSENT_SCOPES.filter(scope=>!desired[scope.key]&&(previousTarget[scope.key]||before[scope.key]));
  // Stop locally before any network operation. Grants require confirmation.
  state={...state,scopes:Object.fromEntries(CONSENT_SCOPES.map(scope=>[scope.key,before[scope.key]&&desired[scope.key]])),targetScopes:desired,pendingScopes:changed.map(scope=>scope.key)};
  emit();
  const session=getFeatureSession();
  if(withdrawn.length&&session) rememberWithdrawal(session,withdrawn.map(scope=>scope.id),PRIVACY_NOTICE_VERSION);
  if(!session&&!wasPending&&!Object.values(desired).some(Boolean)) {
    state={...state,pendingScopes:[],targetScopes:null};emit();return state;
  }
  const save=async()=>{
    if(requestGeneration!==generation)return state;
    if(!getFeatureSession()&&!Object.values(desired).some(Boolean)) {
      state={...state,pendingScopes:[],targetScopes:null};emit();return state;
    }
    const {privacyRequest}=await import('../../utils/api');
    try {
      const [currentResponse, notice]=await Promise.all([
        privacyRequest('/api/privacy/consent'),
        privacyRequest('/api/privacy/config'),
      ]);
      const current=validateConsentState(currentResponse);
      if(requestGeneration!==generation)return state;
      // Manual frontend uploads and backend deployments can happen separately.
      // Only submit a supported notice advertised by this session's authority.
      const noticeVersion=notice?.version;
      if(![PRIVACY_NOTICE_VERSION,LEGACY_PRIVACY_NOTICE_VERSION].includes(noticeVersion))throw new Error('The privacy service is updating. Please retry shortly.');
      const activeSession=getFeatureSession();
      const currentScopes=normalizeScopes(current.scopes);
      // Even if a read still shows off, write the explicit withdrawal. Its
      // revision is a barrier against an earlier grant committing late.
      const updates=CONSENT_SCOPES.filter(scope=>currentScopes[scope.key]!==desired[scope.key]||withdrawn.some(stop=>stop.key===scope.key)||(scope.key==='chat'&&desired.chat&&current.receipts.find(receipt=>receipt.purpose==='si_chat')?.notice_version!==noticeVersion));
      const stops=updates.filter(scope=>!desired[scope.key]);
      if(stops.length)rememberWithdrawal(activeSession,stops.map(scope=>scope.id),PRIVACY_NOTICE_VERSION);
      const result=updates.length?validateConsentState(await privacyRequest('/api/privacy/consent',{
        method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({
          choices:updates.map(scope=>({purpose:scope.id,choice:desired[scope.key]?'granted':'withdrawn',idempotencyKey:crypto.randomUUID()})),
          noticeVersion,expectedRevision:current.revision,
        }),
      })):current;
      if(requestGeneration!==generation)return state;
      const confirmed=normalizeScopes(result.scopes);
      if(CONSENT_SCOPES.some(scope=>confirmed[scope.key]!==desired[scope.key]))throw new Error('The server did not confirm your service choices');
      state=applyResult({...result,chatSaving:confirmed.chat&&noticeVersion===PRIVACY_NOTICE_VERSION&&result.chatSaving!==false});scheduleExpiry(result.expiresAt);
      forgetWithdrawal(CONSENT_SCOPES.map(scope=>scope.id),{token:activeSession.token});emit();return state;
    } catch(error) {
      if(requestGeneration!==generation)return state;
      // A timed-out grant might have committed remotely. Queue a withdrawal,
      // rather than leaving an unconfirmed remote grant behind.
      const uncertain=changed.filter(scope=>desired[scope.key]&&!before[scope.key]);
      const activeSession=getFeatureSession();
      if(uncertain.length&&activeSession)rememberWithdrawal(activeSession,uncertain.map(scope=>scope.id),PRIVACY_NOTICE_VERSION);
      state={...state,scopes:Object.fromEntries(CONSENT_SCOPES.map(scope=>[scope.key,before[scope.key]&&desired[scope.key]])),pendingScopes:[],targetScopes:null,pendingWithdrawal:Boolean(activeSession&&(withdrawn.length||uncertain.length))};
      emit();throw error;
    }
  };
  const pendingSave=saveQueue.catch(()=>{}).then(save);
  saveQueue=pendingSave;
  return pendingSave;
}

export function isConsentGranted(purpose) {
  const key=scopeForPurpose(purpose);
  if(state.scopes?.[key]&&Date.parse(state.expiresAt)<=Date.now()) {
    generation++;clearTimeout(expiryTimer);state={...state,scopes:emptyScopes(),pendingScopes:[],targetScopes:null};emit();
  }
  return Boolean(key && state.scopes?.[key]===true);
}

export function privacyPurposeForRequest(input, methodOverride) {
  try {
    const path=new URL(typeof input==='string'?input:input.url,window.location.origin).pathname;
    const method=methodOverride || (typeof input==='object'&&input?.method) || 'GET';
    const purpose=consentPurposeForRequest(path,method);
    if(purpose) return purpose;
    if(RETIRED_PATH.test(path)) return 'retired';
  } catch {}
  return null;
}

// Refresh only a session that already has an enabled choice; browsing itself
// never creates a feature session or makes a permission request.
if(typeof window!=='undefined') {
  window.addEventListener('focus',async()=>{
    if(state.targetScopes) return;
    if(!Object.values(state.scopes).some(Boolean)&&!state.pendingWithdrawal) return;
    if(state.pendingWithdrawal){completePendingWithdrawal().catch(()=>{});return;}
    const focusGeneration=generation;
    try {
      const {privacyRequest}=await import('../../utils/api');
      const next=await privacyRequest('/api/privacy/consent');
      if(focusGeneration!==generation)return;
      state=applyResult(validateConsentState(next));scheduleExpiry(next.expiresAt);emit();
    } catch {if(focusGeneration!==generation)return;state={...state,scopes:emptyScopes()};emit();}
  });
}

async function completePendingWithdrawal(throwOnError=false) {
  const retryGeneration=generation;
  const session=getFeatureSession();
  const retry=async()=>{
    if(retryGeneration!==generation)return;
    try {
      const {getApiBase}=await import('../../utils/api');
      const result=await retryWithdrawal(getApiBase(),PRIVACY_NOTICE_VERSION);
      if(retryGeneration!==generation||session!==getFeatureSession())return;
      if(result?.ok) {
        // Retried withdrawals can remove permission, never restore a grant or
        // import another tab's session permissions.
        const scopes=normalizeScopes(state.scopes);
        if(result.token===session?.token)for(const scope of CONSENT_SCOPES)scopes[scope.key]=scopes[scope.key]&&result.scopes?.[scope.key]===true;
        state={...state,...(result.token===session?.token&&!result.expired?{revision:result.revision,receipts:result.receipts}:{}),scopes,pendingWithdrawal:false};emit();
      }
    } catch(error) {
      if(retryGeneration!==generation||session!==getFeatureSession())return;
      state={...state,scopes:emptyScopes(),pendingWithdrawal:true};emit();if(throwOnError)throw error;
    }
  };
  const pendingRetry=saveQueue.catch(()=>{}).then(retry);
  saveQueue=pendingRetry;
  return pendingRetry;
}
export function retryPendingWithdrawal() {return completePendingWithdrawal(true);}
if(typeof window !== 'undefined') {
  window.addEventListener('online',completePendingWithdrawal);
  Promise.resolve().then(completePendingWithdrawal);
}
